<template>
  <editor
    ref="editor"
    v-if="instance"
    v-model="instance"
    :is-new="isNew"
    :disabled="!editing"
    :errors="validationErrors"
    @cancel="cancel"
    @edit="edit"
    @removed="removed"
    @remove-error="handleError"
    @reset-errors="resetErrors"
    @saved="saved"
    @save-error="handleError"
    @load="load"
  >
    <div slot="buttons">
      <!--
      ==========================================================================
      Buttons > Cancel
      ==========================================================================
      -->
      <b-button variant="cancel" class="float-left" @click.prevent="cancel()">
        <i class="fa fa-fw fa-arrow-left"></i>
        <span class="d-sm-inline d-none">{{ $t("common.cancel") }}</span>
      </b-button>

      <!--
      ==========================================================================
      Buttons > Actions
      ==========================================================================
      -->
      <b-button variant="primary" @click.prevent="$refs.actionsSheet.show()">
        <i class="fa fa-fw fa-ellipsis-v"></i>
        <span class="d-sm-inline d-none">{{ $t("common.actions") }}</span>
      </b-button>

      <!--
      ==========================================================================
      Buttons > Actions Sheet
      ==========================================================================
      -->
      <modal-sheet ref="actionsSheet">
        <b-button
          v-if="instance.permissions.saveInitial && !editing"
          block
          variant="warning"
          @click.prevent="
            edit();
            $refs.actionsSheet.hide();
          "
        >
          {{ $t("common.edit") }}
        </b-button>

        <b-button
          v-if="instance.permissions.submit"
          variant="success"
          block
          @click.prevent="submitRequest()"
        >
          {{ $t("common.submit") }}
        </b-button>

        <b-button
          v-if="instance.permissions.saveInitial && editing"
          block
          variant="warning"
          @click.prevent="saveInitial()"
        >
          {{ $t("request.actions.saveInitial") }}
        </b-button>

        <b-button
          v-if="instance.permissions.assign"
          block
          variant="primary"
          @click.prevent="assign()"
        >
          {{ $t("common.assign") }}
        </b-button>

        <b-button
          block
          variant="cancel"
          @click.prevent="$refs.actionsSheet.hide()"
        >
          {{ $t("common.cancel") }}
        </b-button>
      </modal-sheet>

      <!--
      ==========================================================================
      Buttons > Debug
      ==========================================================================
      -->
      <b-button v-if="!$env.production" variant="dark" v-b-modal.modal-debug>
        <i class="fa fa-fw fa-code"></i>
        <span class="d-sm-inline d-none">{{ $t("common.debug") }}</span>
      </b-button>

      <!--
      ==========================================================================
      Buttons > Remove
      ==========================================================================
      -->
      <b-button
        v-if="instance.permissions.delete"
        variant="remove"
        @click.prevent="$refs.removeSheet.show()"
      >
        <i class="fa fa-fw fa-times"></i>
        <span class="d-sm-inline d-none">{{ $t("common.remove") }}</span>
      </b-button>

      <!--
      ==========================================================================
      Buttons > Remove Sheet
      ==========================================================================
      -->
      <modal-sheet ref="removeSheet">
        <b-button block variant="danger" @click.prevent="remove()">
          {{ $t("common.remove") }}
        </b-button>
        <b-button
          block
          variant="cancel"
          @click.prevent="$refs.removeSheet.hide()"
        >
          {{ $t("common.cancel") }}
        </b-button>
      </modal-sheet>
    </div>
  </editor>
</template>
<script>
import LoadPageMixin from "vue-template/protected/mixins/LoadPageMixin";
import pluralize from "pluralize";
import lowerFirst from "lodash/lowerFirst";
import { uuid } from "core/utils";

export default {
  mixins: [LoadPageMixin],

  methods: {
    getService() {
      throw Error("Virtual method");
    },

    getLabel(/*instance*/) {
      throw Error("Virtual method");
    },

    breadcrumbs() {
      const routeName = this.$route.name;

      return [
        {
          icon: "home",
          text: "Home",
          to: "/protected"
        },
        {
          text: () => this.$t("menu." + lowerFirst(pluralize(routeName))),
          to: { name: pluralize(routeName) }
        },
        {
          text:
            this.$route.params.id === "new"
              ? "<New>"
              : this.getLabel(this.instance) || "<New>"
        }
      ];
    },

    edit() {
      const params = { id: this.instance.id };
      const query = { edit: true };
      this.$router.push({ name: this.$route.name, params, query });
    },

    cancel() {
      this.$router.push({ name: pluralize(this.$route.name) });
    },

    removed(/*instance*/) {
      this.$router.push({ name: pluralize(this.$route.name) });
    },

    async saved(instance) {
      const params = { id: instance.id, edit: "false" };
      await this.$router.push({
        name: this.$route.name,
        params,
        query: { rnd: uuid() }
      });
    },

    variantFor(s) {
      switch (s.type) {
        case "INTERMEDIATE":
          return "warning";
        case "SUCCESS":
          return "success";
        case "FAILURE":
          return "danger";
      }
      throw Error("Unsupported type");
    },

    nextActorFor(s) {
      switch (s) {
        case "ADMIN":
          return "all'amministratore di sistema";
        case "CUSTOMER":
          return "al cliente";
        case "PROVIDER":
          return "al fornitore";
        case "BROKER":
          return "al gestore";
        default:
          throw Error("Unsupported");
      }
    },

    async saveInitial() {
      await this.$refs.editor.save();
      this.$refs.actionsSheet.hide();
    },

    async submitRequest() {
      //console.log('Going to status', s);

      const msg =
        "Questa azione sottometterà la richiesta al sistema." +
        " Lo stato della richiesta diverrà 'inviata' ed" +
        " i parametri iniziali non saranno più modificabili." +
        " Sei sicuro di voler procedere?";

      /*
      let nextActor = s.primaryActor.map(this.nextActorFor).join(' oppure ');
      const msg = "Questa azione manderà la richiesta in stato "
                + s.label
                + " dove il controllo passerà "
                + nextActor
                + ". Sei sicuro di voler procedere?";*/

      if (!confirm(msg)) {
        return;
      }

      this.$refs.actionsSheet.hide();
      return await this.$refs.editor.save({
        status: "SENT"
      });
    },

    assign() {
      this.$router.push({
        //  'CreditAssignmentRequest' -> 'CreditAssignmentEvaluation'
        name: this.$route.name.replace("Request", "Evaluation"),
        params: { id: this.instance.id }
      });
    },

    async remove() {
      await this.$refs.editor.remove();
      this.$refs.removeSheet.hide();
    }
  }
};
</script>
